import * as React from 'react'
import Supercluster from 'supercluster'
import type { MapPoint } from '@/lib/types'

export interface ClusterFeature {
  type: 'cluster'
  id: number
  lng: number
  lat: number
  pointCount: number
}

export interface PointFeature {
  type: 'point'
  id: string
  lng: number
  lat: number
  point: MapPoint
}

export type MapFeature = ClusterFeature | PointFeature

type PointProps = { pointId: string; point: MapPoint }
type ClusterPoint = Supercluster.PointFeature<PointProps>

/**
 * Clusters the FULL match set for the map view.
 *
 * Previously this took the 100-listing page the map happened to have loaded and
 * — because 100 pins always fit — returned every one of them as an individual
 * pin, aggregation disabled. That is what made pagination necessary: the map
 * could never show more than one page at a time.
 *
 * Now it indexes every matching listing's coordinates (fetched once per search
 * via /search/map-points) and lets supercluster do its job: zoomed out you get
 * a handful of cluster bubbles with exact counts, and each zoom step splits
 * them until individual price pins remain. No refetching, no pages.
 */
export function useMapClusters(mapPoints: MapPoint[]) {
  const indexRef = React.useRef<Supercluster<PointProps> | null>(null)

  const points = React.useMemo<ClusterPoint[]>(
    () =>
      mapPoints
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
        .map((p) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] },
          properties: { pointId: p.id, point: p },
        })),
    [mapPoints]
  )

  const index = React.useMemo(() => {
    const sc = new Supercluster<PointProps>({
      radius: 60,
      // Past this zoom supercluster stops aggregating and every point stands
      // alone — the "zoom in far enough and you see each flat" end state.
      maxZoom: 16,
      minPoints: 2,
    })
    sc.load(points)
    indexRef.current = sc
    return sc
  }, [points])

  const getClusters = React.useCallback(
    (bbox: [number, number, number, number], zoom: number): MapFeature[] =>
      index.getClusters(bbox, Math.round(zoom)).map((f): MapFeature => {
        const [lng, lat] = f.geometry.coordinates
        // supercluster tags aggregates with properties.cluster === true
        if ((f.properties as Supercluster.ClusterProperties).cluster) {
          const cp = f.properties as Supercluster.ClusterProperties
          return { type: 'cluster', id: cp.cluster_id, lng, lat, pointCount: cp.point_count }
        }
        const pp = f.properties as PointProps
        return { type: 'point', id: pp.pointId, lng, lat, point: pp.point }
      }),
    [index]
  )

  /**
   * Ids of the individual listings inside the viewport, cluster membership
   * ignored — this is what the side panel lists, so it must include listings
   * that are still visually merged into a bubble. Capped: the panel is a
   * scrollable list, not a data dump, and hydrating hundreds of cards would
   * undo the point of the skinny payload.
   */
  const getVisibleIds = React.useCallback(
    (bbox: [number, number, number, number], cap = 60): string[] => {
      const [west, south, east, north] = bbox
      const ids: string[] = []
      for (const p of points) {
        const [lng, lat] = p.geometry.coordinates
        if (lng >= west && lng <= east && lat >= south && lat <= north) {
          ids.push(p.properties.pointId)
          if (ids.length >= cap) break
        }
      }
      return ids
    },
    [points]
  )

  /** Total in the viewport, uncapped — the panel's "showing N of M". */
  const countInBounds = React.useCallback(
    (bbox: [number, number, number, number]): number => {
      const [west, south, east, north] = bbox
      let n = 0
      for (const p of points) {
        const [lng, lat] = p.geometry.coordinates
        if (lng >= west && lng <= east && lat >= south && lat <= north) n++
      }
      return n
    },
    [points]
  )

  /**
   * Smallest zoom at which `pointId` renders as its OWN pin rather than inside
   * a cluster — probes the index upward from `fromZoom` in a tiny bbox around
   * the point. Used by "bring this listing into view" so one hover lands on
   * the pin instead of stepping in by one zoom and hoping. Falls back to the
   * index's maxZoom+1 (where supercluster never aggregates).
   */
  const zoomToIsolate = React.useCallback(
    (pointId: string, lat: number, lng: number, fromZoom: number): number => {
      const eps = 0.0005
      const bbox: [number, number, number, number] = [lng - eps, lat - eps, lng + eps, lat + eps]
      for (let z = Math.max(0, Math.round(fromZoom)); z <= 17; z++) {
        const alone = index
          .getClusters(bbox, z)
          .some((f) => !(f.properties as Supercluster.ClusterProperties).cluster && (f.properties as PointProps).pointId === pointId)
        if (alone) return z
      }
      return 17
    },
    [index]
  )

  const getClusterExpansionZoom = React.useCallback((clusterId: number): number => {
    const sc = indexRef.current
    if (!sc) return 16
    try {
      return sc.getClusterExpansionZoom(clusterId)
    } catch {
      return 16
    }
  }, [])

  return { getClusters, getClusterExpansionZoom, getVisibleIds, countInBounds, zoomToIsolate }
}
